import axios, { CancelTokenSource } from 'axios';
import { ActionContext } from 'vuex';

export interface ApiResult<T> {
    result: string;
    resultCode?: string;
    data?: T;
}

export function apiFailure<T>(err?: any): ApiResult<T> {
    if (err) {
        console.log(err);
    }
    return { result: 'ERROR' };
}

export interface ApiOptions {
    base: string;
    checkVersionHeader: boolean;
    authenticationEndPoint: string;
    authenticationClientId: string;
}

const options: ApiOptions = {
    base: '',
    checkVersionHeader: true,
    authenticationEndPoint: '',
    authenticationClientId: ''
}

function serializeParameters(params: any) {
    let search = [];
    for (let k in params) {
        let v = params[k];
        if (v && Array.isArray(v)) {
            search.push(...v.map(x => `${k}=${encodeURIComponent(x)}`));
        }
        else {
            search.push(`${k}=${encodeURIComponent(v)}`);
        }
    }
    return search.join('&');
}

const http = axios.create({
    paramsSerializer: serializeParameters
});

export function init(initAction: (options: ApiOptions) => void) {
    if (window) {
        let l = window.location;
        options.base = `${l.protocol}//${l.host}`
    }

    if (initAction) {
        initAction(options);
    }

    if (options.checkVersionHeader) {
        http.interceptors.response.use(function (res) {
            let c = VERSION;
            let s = res.headers["x-application-version"];
            if (s && c && s != c && !s.endsWith('DEVEL')) {
                window.location.reload(true);
                console.log(`Version mismatch (s:${s} != c:${c}): reloading application ...`);
            }
            return res;
        });
    }
}

function buildRequest(url: string, volatile: boolean, parameters?: any): any {
    url = url[0] == '/' ? `${options.base}${url}` : url;

    let params: any = {};
    if (parameters) {
        for (let k in parameters) {
            if (k == '__volatile') {
                volatile = true;
            }
            else {
                params[k] = parameters[k];
            }
        }
    }
    if (volatile) {
        params['__t'] = new Date().getTime();
    }

    // Return ready to go Axios options.
    return { url, params };
}

const discardableRequests: { [url: string]: CancelTokenSource } = {};

function is401(error: any) {
    console.log(error)
    if (error.response && error.response.status == 401) {
        if (window) {
            window.location.href = "/";
        }
        return true;
     }
     return false;
}

async function addAuthorization(token?: string) {
    const headers: any = {};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
}

export async function get(url: string, parameters?: any, token: string = null, isDiscardable = false) {
    let cts: CancelTokenSource;

    if (parameters === true || isDiscardable) {
        cts = discardableRequests[url];
        if (cts) {
            cts.cancel();
        }
        cts = discardableRequests[url] = axios.CancelToken.source();
    }

    return http
        .request({
            method: 'GET',
            cancelToken: cts && cts.token || null,
            headers: await addAuthorization(token),
            ...buildRequest(url, true, parameters)
        })
        .catch((error: any) => {
            if (is401(error)) {
                return error.response;
            }
            throw error;
       });
}

export async function getAsDataURL(url: string, parameters?: any): Promise<string> {
    return http
        .request({
            method: 'GET',
            responseType: 'blob',
            ...buildRequest(url, true, parameters) })
        .then(res => new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(<string>reader.result);
            reader.onerror = reject
            reader.readAsDataURL(res.data)
        }))
        .catch((error: any) => {
            if (is401(error)) {
                return error.response;
            }
            throw error;
       });
}

export async function post(url: string, data?: any, token?: string) {
    return http
        .request({
            method: 'POST',
            data,
            headers: await addAuthorization(token),
            ...buildRequest(url, false)
        })
        .catch((error: any) => {
            if (is401(error)) {
                return error.response;
            }
            throw error;
        });
}

export async function put(url: string, data?: any, headers?: any) {
    return http
        .request({ method: 'PUT', data, headers, ...buildRequest(url, false) })
        .catch((error: any) => {
            if (is401(error)) {
                return error.response;
            }
            throw error;
        });
}

export async function del(url: string, parameters?: any, headers?: any) {
    return http
        .request({ method: 'DELETE', headers, ...buildRequest(url, false, parameters) })
        .catch((error: any) => {
            if (is401(error)) {
                return error.response;
            }
            throw error;
       });
}

export function checkResult(result: ApiResult<void>, ctx?: ActionContext<any,any>): boolean;

export function checkResult<T>(result: ApiResult<T>, ctx?: ActionContext<any,any>): T | false;

export function checkResult<T>(result: ApiResult<T>, ctx?: ActionContext<any,any>) {
    if (result) {
        if (result.result == "OK") {
            return result.data || true;
        }
        else {
            if (result.result == "ERROR") {
                //ctx.dispatch('showError', result);
            }
        }
    }
    return false;
}